import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { isClusterManaged } from '@cast/shared-cluster-context-data-access';
import { Cloud, K8sProvider } from '@cast/shared-types';
import type { DateTime, TendencyChange } from '@cast/shared-types';
import {
  Cluster,
  ClusterDisplayStatus,
  ClusterHourlyCost,
  ClusterStatus,
} from '@cast/types';
import { getTendencyChange, toBig } from '@cast/utils';

import { mapClusterToCloud } from 'utils/csp';

import { ClusterListEntity, MonthlyCosts } from './types';

const getClusterMonthlyCostOfDay = (
  hourlyCosts: ClusterHourlyCost[],
  day: DateTime
): MonthlyCosts | undefined => {
  const todayCost = hourlyCosts.find(({ timestamp }) =>
    dayjs(timestamp).isSame(day, 'day')
  );
  if (!todayCost) {
    return undefined;
  }
  const onDemandCost = toBig(todayCost.costOnDemandPerHour || 0).mul(
    AVERAGE_HOURS_IN_MONTH
  );
  const spotCost = toBig(todayCost.costSpotPerHour || 0).mul(
    AVERAGE_HOURS_IN_MONTH
  );
  const fallbackCost = toBig(todayCost.costSpotFallbackPerHour || 0).mul(
    AVERAGE_HOURS_IN_MONTH
  );
  return {
    monthlyOnDemand: onDemandCost.toNumber(),
    monthlySpot: spotCost.toNumber(),
    monthlyFallback: fallbackCost.toNumber(),
    monthlyTotal: onDemandCost.add(spotCost).add(fallbackCost).toNumber(),
  };
};

export const mapClusterToList = (
  cluster: Cluster,
  hourlyCosts?: ClusterHourlyCost[]
): ClusterListEntity => {
  const status: ClusterDisplayStatus = cluster.displayStatus;
  const providerNamespaceId: string | undefined = cluster.providerNamespaceId;
  const provider: K8sProvider | undefined = cluster.providerType;
  const isManaged = isClusterManaged(cluster);
  const clouds: Cloud[] = [];
  const error: string | undefined = cluster.reconcileError;
  const reconcileError: string | undefined = cluster.reconcileError;
  const agentSnapshotReceivedAt: string | undefined =
    cluster.agentSnapshotReceivedAt;
  let monthlyCosts: MonthlyCosts | undefined;
  let costTendencyChange: TendencyChange | undefined;

  const mappedCloud = mapClusterToCloud(cluster);
  if (mappedCloud) {
    clouds.push(mappedCloud);
  }
  if (hourlyCosts) {
    // Ref: https://castai.atlassian.net/wiki/spaces/ENG/pages/2022473758/RFC+Time+range+interpretation
    // in order to use latest data for today, timestamp of beginning of tommorow in UTC is used
    const today = dayjs().add(1, 'day').startOf('day').utc();
    const yesterday = today.subtract(1, 'day');
    const monthlyCostToday = getClusterMonthlyCostOfDay(hourlyCosts, today);
    const monthlyCostYesterday = getClusterMonthlyCostOfDay(
      hourlyCosts,
      yesterday
    );
    monthlyCosts = monthlyCostToday;
    if (monthlyCostToday && monthlyCostYesterday) {
      costTendencyChange = getTendencyChange(
        monthlyCostToday.monthlyTotal,
        monthlyCostYesterday.monthlyTotal,
        { percentage: true }
      );
    }
  }
  if (cluster.status === ClusterStatus.DISCOVERED) {
    const costFromTags =
      cluster.tags?.['cloud-discovery-total-monthly-cost-usd'];
    monthlyCosts = {
      monthlyOnDemand: 0,
      monthlySpot: 0,
      monthlyFallback: 0,
      monthlyTotal: costFromTags ? Number(costFromTags) : 0,
    };
    costTendencyChange = { diff: 0, tendency: 'stable' };
  }

  return {
    id: cluster.id,
    name: cluster.name,
    clouds,
    agentSnapshotReceivedAt,
    provider,
    providerNamespaceId,
    status,
    region: cluster.region,
    error,
    reconcileError,
    isManaged,
    original: cluster,
    monthlyCosts,
    costTendencyChange,
  };
};

type ClusterListOrderItem = {
  status?: ClusterDisplayStatus | ClusterStatus;
  displayStatus?: ClusterDisplayStatus;
};

const getClusterListOrderStatus = (
  cluster: ClusterListOrderItem
): ClusterDisplayStatus | ClusterStatus | undefined =>
  cluster.displayStatus ?? cluster.status;

export const orderClustersForDashboardList = <T extends ClusterListOrderItem>(
  clusters: T[]
): T[] =>
  orderBy(
    clusters,
    [
      (cluster): number =>
        getClusterListOrderStatus(cluster) === ClusterDisplayStatus.DISCOVERED
          ? 0
          : 1,
      (cluster): number =>
        getClusterListOrderStatus(cluster) === ClusterDisplayStatus.FAILED
          ? 1
          : 0,
    ],
    ['desc', 'desc']
  );
